import React, { useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Tabs,
  DatePicker,
  Row,
  Col,
  Statistic,
  Badge,
  Tooltip,
  Divider,
  Spin,
  Image,
  Alert,
} from 'antd';
import {
  EditOutlined,
  EyeOutlined,
  DollarOutlined,
  ShoppingCartOutlined,
  TruckOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  PrinterOutlined,
  DownloadOutlined,
  ShareAltOutlined,
  CheckOutlined,
  QrcodeOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import RefundModal from '../components/RefundModal';
import { downloadInvoicePdf } from '../utils/invoiceDownload';

const { Option } = Select;

const Orders = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [isBillingModalVisible, setIsBillingModalVisible] = useState(false);
  const [isRefundModalVisible, setIsRefundModalVisible] = useState(false);
  const [refundingOrder, setRefundingOrder] = useState(null);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [assigningDeliveryId, setAssigningDeliveryId] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [statusForm] = Form.useForm();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [activeTab, setActiveTab] = useState('orders');
  const queryClient = useQueryClient();

  // Fetch orders with filters
  const { data: orders = [], isLoading } = useQuery(
    ['orders', searchTerm, statusFilter, dateRange],
    async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      if (dateRange && dateRange.length === 2) {
        params.append('start_date', dateRange[0].format('YYYY-MM-DD'));
        params.append('end_date', dateRange[1].format('YYYY-MM-DD'));
      }

      params.append('per_page', '100');
      const response = await api.get(`/admin/orders?${params.toString()}`);
      const data = response.data?.data ?? response.data ?? [];
      return Array.isArray(data) ? data : [];
    },
    {
      staleTime: 0,
      refetchOnWindowFocus: true,
      onError: (_error) => {
        message.error('Failed to load orders');
      },
    },
  );

  // Fetch order statistics
  const { data: orderStats } = useQuery(
    'order-stats',
    async () => {
      const response = await api.get('/admin/orders/stats');
      const data = response.data?.data ?? response.data ?? {};
      return data;
    },
    {},
  );

  // Fetch billing data
  const { data: billingData = [], isLoading: billingLoading } = useQuery(
    'billing-data',
    async () => {
      const response = await api.get('/admin/billing/invoices');
      const data = response.data?.data ?? response.data ?? [];
      return Array.isArray(data) ? data : [];
    },
    {},
  );

  // Update order status mutation
  const updateOrderStatusMutation = useMutation(
    async ({ id, status }) => {
      const response = await api.put(`/admin/orders/${id}/status`, { order_status: status });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Order status updated successfully');
        queryClient.invalidateQueries('orders');
        setIsModalVisible(false);
        statusForm.resetFields();
      },
      onError: (error) => {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to update order status';
        message.error(msg);
      },
    },
  );

  // Cancel order mutation
  const cancelOrderMutation = useMutation(
    async (id) => {
      const response = await api.post(`/admin/orders/${id}/cancel`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Order cancelled successfully');
        queryClient.invalidateQueries('orders');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to cancel order');
      },
    },
  );

  // Complete order mutation
  const completeOrderMutation = useMutation(
    async (id) => {
      const response = await api.post(`/admin/orders/${id}/complete`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Order completed successfully');
        queryClient.invalidateQueries('orders');
      },
      onError: (error) => {
        const msg =
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Failed to complete order';
        message.error(msg);
      },
    },
  );

  // Generate invoice mutation
  const generateInvoiceMutation = useMutation(
    async (orderId) => {
      const response = await api.post(`/admin/billing/orders/${orderId}/generate-bill`);
      return response.data;
    },
    {
      onSuccess: (response) => {
        const invoice = response?.data;
        if (response?.already_exists && invoice) {
          const label = invoice.invoice_number || `#${invoice.id}`;
          message.info(`Invoice ${label} already exists for this order`, 5);
          downloadInvoicePdf(invoice, 'invoice');
        } else {
          message.success('Invoice generated successfully');
        }
        queryClient.invalidateQueries('billing-data');
      },
      onError: (error) => {
        const serverMsg = error.response?.data?.message;
        if (serverMsg && serverMsg.includes('already exists')) {
          message.info('Invoice already exists for this order');
        } else {
          message.error('Failed to generate invoice' + (serverMsg ? ': ' + serverMsg : ''));
        }
      },
    },
  );

  // Verify payment mutation
  const verifyPaymentMutation = useMutation(
    async (orderId) => {
      const response = await api.post(`/admin/payments/${orderId}/verify`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Payment verified successfully');
        queryClient.invalidateQueries('orders');
        queryClient.invalidateQueries('order-stats');
        setIsDetailModalVisible(false);
      },
      onError: (error) => {
        message.error(
          'Failed to verify payment: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  // Reject payment mutation
  const rejectPaymentMutation = useMutation(
    async ({ orderId, reason }) => {
      const response = await api.post(`/admin/payments/${orderId}/reject`, { reason });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Payment rejected. Customer can resubmit.');
        queryClient.invalidateQueries('orders');
        setIsDetailModalVisible(false);
      },
      onError: (error) => {
        message.error(
          'Failed to reject payment: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  // Mark as paid mutation
  const markAsPaidMutation = useMutation(
    async (orderId) => {
      const response = await api.post(`/admin/orders/${orderId}/mark-paid`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Order marked as paid');
        queryClient.invalidateQueries('orders');
        queryClient.invalidateQueries('order-stats');
      },
      onError: (error) => {
        message.error(
          'Failed to mark as paid: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  // Fetch delivery personnel for assignment
  const { data: deliveryPersonnel = [] } = useQuery(
    'delivery-personnel-list',
    async () => {
      const response = await api.get('/admin/delivery-personnel');
      return response.data?.data || response.data || [];
    },
    {
      staleTime: 60000,
    },
  );

  // Assign delivery mutation
  const assignDeliveryMutation = useMutation(
    async ({ orderId, deliveryPersonId }) => {
      const response = await api.post(`/admin/orders/${orderId}/assign-delivery`, {
        delivery_person_id: deliveryPersonId,
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        message.success(data?.message || 'Delivery assigned successfully');
        queryClient.invalidateQueries('orders');
        setViewingOrder((prev) => ({
          ...prev,
          delivery_person: {
            id: data?.data?.delivery_person_id,
            full_name: data?.data?.delivery_person_name,
          },
        }));
        setAssigningDeliveryId(null);
      },
      onError: (error) => {
        message.error(error.response?.data?.message || 'Failed to assign delivery');
      },
    },
  );

  // Valid next statuses for admin — can jump to any status
  const getValidNextStatuses = (currentStatus) => {
    if (currentStatus === 'completed') {
      return ['partially_refunded'];
    }
    const allStatuses = [
      'pending',
      'accepted',
      'packaging',
      'out_for_delivery',
      'delivered',
      'completed',
      'cancelled',
      'refunded',
      'partially_refunded',
    ];
    return allStatuses.filter((s) => s !== currentStatus);
  };

  // Handle functions
  const isOrderFinalized = (record) => {
    return (
      (record.order_status === 'delivered' || record.order_status === 'completed') &&
      record.payment_status === 'paid'
    );
  };

  const getRowClassName = (record) => {
    if (record.order_status === 'cancelled') return 'order-row-cancelled';
    if (record.order_status === 'refunded') return 'order-row-refunded';
    if (record.order_status === 'partially_refunded') return 'order-row-partially-refunded';
    if (isOrderFinalized(record)) return '';
    return 'order-row-warning';
  };

  const hasServiceItems = (record) => {
    if (!record.items) return false;
    return record.items.some((item) => item.product?.type === 'service');
  };

  const handleView = (record) => {
    setViewingOrder(record);
    setIsDetailModalVisible(true);
  };

  const handleUpdateStatus = (record) => {
    setEditingOrder(record);
    statusForm.setFieldsValue({
      current_status: record.order_status,
    });
    setIsModalVisible(true);
  };

  const handleStatusUpdate = (values) => {
    updateOrderStatusMutation.mutate({
      id: editingOrder.id,
      status: values.order_status,
    });
  };

  const handleCancel = (record) => {
    cancelOrderMutation.mutate(record.id);
  };

  const handleComplete = (record) => {
    completeOrderMutation.mutate(record.id);
  };

  const handleGenerateInvoice = (record) => {
    generateInvoiceMutation.mutate(record.id);
  };

  const handleBilling = (record) => {
    setViewingOrder(record);
    setIsBillingModalVisible(true);
  };

  const handleProcessRefund = (record) => {
    setRefundingOrder(record);
    setIsRefundModalVisible(true);
  };

  const getRefundStatusColor = (refundStatus) => {
    const colorMap = {
      none: 'default',
      partial_refund_pending: 'orange',
      partially_refunded: 'blue',
      fully_refunded: 'green',
    };
    return colorMap[refundStatus] || 'default';
  };

  const canProcessRefund = (record) => {
    return (
      ['delivered', 'completed', 'partially_refunded'].includes(record.order_status) &&
      record.payment_status === 'paid'
    );
  };

  const columns = [
    {
      title: 'Order ID',
      dataIndex: 'order_number',
      key: 'order_number',
      render: (text) => <strong>#{text}</strong>,
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      render: (customer) => customer?.name || customer?.full_name || 'N/A',
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      render: (provider) => provider?.name || provider?.full_name || 'N/A',
    },
    {
      title: 'Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => `Rs. ${parseFloat(amount || 0).toFixed(2)}`,
      sorter: (a, b) => parseFloat(a.total_amount || 0) - parseFloat(b.total_amount || 0),
    },
    {
      title: 'Delivery Charge',
      dataIndex: 'delivery_charge',
      key: 'delivery_charge',
      render: (charge) => `Rs. ${parseFloat(charge || 0).toFixed(2)}`,
    },
    {
      title: 'Status',
      dataIndex: 'order_status',
      key: 'order_status',
      render: (status) => {
        const colorMap = {
          payment_pending: 'purple',
          pending: 'orange',
          accepted: 'blue',
          packaging: 'purple',
          out_for_delivery: 'cyan',
          delivered: 'green',
          completed: 'green',
          canceled: 'red',
          cancelled: 'red',
          refunded: 'red',
          partially_refunded: 'blue',
        };
        const iconMap = {
          payment_pending: <QrcodeOutlined />,
          pending: <ClockCircleOutlined />,
          accepted: <CheckCircleOutlined />,
          packaging: <FileTextOutlined />,
          out_for_delivery: <TruckOutlined />,
          delivered: <CheckCircleOutlined />,
          completed: <CheckCircleOutlined />,
          canceled: <CloseCircleOutlined />,
          cancelled: <CloseCircleOutlined />,
          refunded: <ExclamationCircleOutlined />,
          partially_refunded: <RollbackOutlined />,
        };
        return (
          <Tag color={colorMap[status] || 'default'} icon={iconMap[status]}>
            {status === 'payment_pending'
              ? 'PAYMENT PENDING'
              : status?.replace('_', ' ').toUpperCase()}
          </Tag>
        );
      },
      filters: [
        { text: 'Payment Pending', value: 'payment_pending' },
        { text: 'Pending', value: 'pending' },
        { text: 'Accepted', value: 'accepted' },
        { text: 'Packaging', value: 'packaging' },
        { text: 'Out for Delivery', value: 'out_for_delivery' },
        { text: 'Delivered', value: 'delivered' },
        { text: 'Completed', value: 'completed' },
        { text: 'Cancelled', value: 'cancelled' },
        { text: 'Refunded', value: 'refunded' },
        { text: 'Partially Refunded', value: 'partially_refunded' },
      ],
      onFilter: (value, record) => record.order_status === value,
    },
    {
      title: 'Payment Status',
      dataIndex: 'payment_status',
      key: 'payment_status',
      render: (status, _record) => {
        const colorMap = {
          pending: 'orange',
          verification_pending: 'blue',
          paid: 'green',
          failed: 'red',
          refunded: 'red',
        };
        const iconMap = {
          pending: <ClockCircleOutlined />,
          verification_pending: <ExclamationCircleOutlined />,
          paid: <CheckCircleOutlined />,
          failed: <CloseCircleOutlined />,
        };
        const labelMap = {
          pending: 'PENDING',
          verification_pending: 'VERIFY',
          paid: 'PAID',
          failed: 'FAILED',
        };
        return (
          <Space>
            <Tag color={colorMap[status] || 'default'} icon={iconMap[status]}>
              {labelMap[status] || status?.toUpperCase()}
            </Tag>
            {status === 'verification_pending' && (
              <Tooltip title="Needs verification">
                <Badge count={<ExclamationCircleOutlined style={{ color: '#faad14' }} />} />
              </Tooltip>
            )}
          </Space>
        );
      },
      filters: [
        { text: 'Pending', value: 'pending' },
        { text: 'Awaiting Verification', value: 'verification_pending' },
        { text: 'Paid', value: 'paid' },
        { text: 'Failed', value: 'failed' },
      ],
      onFilter: (value, record) => record.payment_status === value,
    },
    {
      title: 'Refund Status',
      key: 'refund_status',
      render: (_, record) => (
        <Tag color={getRefundStatusColor(record.refund_status)}>
          {record.refund_status_display || 'No Refund'}
        </Tag>
      ),
      filters: [
        { text: 'No Refund', value: 'none' },
        { text: 'Partial Refund Pending', value: 'partial_refund_pending' },
        { text: 'Partially Refunded', value: 'partially_refunded' },
        { text: 'Fully Refunded', value: 'fully_refunded' },
      ],
      onFilter: (value, record) => record.refund_status === value,
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button type="text" icon={<EyeOutlined />} onClick={() => handleView(record)} />
          </Tooltip>
          <Tooltip title="Update Status">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleUpdateStatus(record)}
            />
          </Tooltip>
          <Tooltip title="Generate Invoice">
            <Button
              type="text"
              icon={<FileTextOutlined />}
              onClick={() => handleGenerateInvoice(record)}
            />
          </Tooltip>
          {(record.order_status === 'pending' || record.order_status === 'accepted') && (
            <Tooltip title="Cancel Order">
              <Button
                type="text"
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => handleCancel(record)}
              />
            </Tooltip>
          )}
          {(record.order_status === 'delivered' || record.order_status === 'out_for_delivery') && (
            <Tooltip title="Complete Order">
              <Button
                type="text"
                icon={<CheckCircleOutlined />}
                onClick={() => handleComplete(record)}
              />
            </Tooltip>
          )}
          <Tooltip title="View Billing">
            <Button type="text" icon={<DollarOutlined />} onClick={() => handleBilling(record)} />
          </Tooltip>
          {record.payment_status !== 'paid' && (
            <Tooltip title="Mark as Paid">
              <Button
                type="text"
                icon={<CheckCircleOutlined />}
                onClick={() => markAsPaidMutation.mutate(record.id)}
              />
            </Tooltip>
          )}
          {canProcessRefund(record) && (
            <Tooltip title="Process Refund">
              <Button
                type="text"
                danger
                icon={<RollbackOutlined />}
                onClick={() => handleProcessRefund(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // Billing columns
  const billingColumns = [
    {
      title: 'Invoice ID',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      render: (text) => <strong>#{text}</strong>,
    },
    {
      title: 'Order ID',
      dataIndex: 'order_number',
      key: 'order_number',
      render: (text) => `#${text}`,
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      render: (customer) => customer?.name || customer?.full_name || 'N/A',
    },
    {
      title: 'Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount, record) => {
        const refunded = parseFloat(record.refunded_amount || 0);
        if (refunded > 0) {
          const net = parseFloat(record.net_amount || record.total_amount || 0);
          return (
            <Space direction="vertical" size={0}>
              <span style={{ fontSize: 12, textDecoration: 'line-through', color: '#999' }}>
                Rs. {parseFloat(amount || 0).toFixed(2)}
              </span>
              <span style={{ color: '#52c41a', fontWeight: 'bold' }}>Rs. {net.toFixed(2)}</span>
              <Tag color="red" style={{ fontSize: 10, lineHeight: '16px' }}>
                -Rs. {refunded.toFixed(2)}
              </Tag>
            </Space>
          );
        }
        return `Rs. ${parseFloat(amount || 0).toFixed(2)}`;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colorMap = {
          draft: 'orange',
          sent: 'blue',
          paid: 'green',
          overdue: 'red',
          cancelled: 'red',
        };
        return <Tag color={colorMap[status] || 'default'}>{status?.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Due Date',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (date) => (date ? new Date(date).toLocaleDateString() : 'N/A'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Download PDF">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={() =>
                window.open(`/admin/billing/invoices/${record.id}/pdf/download`, '_blank')
              }
            />
          </Tooltip>
          <Tooltip title="Print">
            <Button
              type="text"
              icon={<PrinterOutlined />}
              onClick={() => window.open(`/admin/billing/invoices/${record.id}/pdf`, '_blank')}
            />
          </Tooltip>
          <Tooltip title="Share">
            <Button
              type="text"
              icon={<ShareAltOutlined />}
              onClick={() => window.open(`/admin/billing/invoices/${record.id}/share`, '_blank')}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Order Statistics */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Orders"
              value={orderStats?.total_orders || 0}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Pending Orders"
              value={orderStats?.pending_orders || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Completed Orders"
              value={orderStats?.completed_orders || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Revenue"
              value={orderStats?.total_revenue || 0}
              prefix={<DollarOutlined />}
              precision={2}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Card
        title="Order Management"
        extra={
          <Space>
            <Input.Search
              placeholder="Search orders..."
              allowClear
              style={{ width: 250 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Select
              placeholder="Filter by status"
              allowClear
              style={{ width: 150 }}
              onChange={setStatusFilter}
              options={[
                { label: 'Payment Pending', value: 'payment_pending' },
                { label: 'Pending', value: 'pending' },
                { label: 'Accepted', value: 'accepted' },
                { label: 'Packaging', value: 'packaging' },
                { label: 'Out for Delivery', value: 'out_for_delivery' },
                { label: 'Delivered', value: 'delivered' },
                { label: 'Completed', value: 'completed' },
                { label: 'Cancelled', value: 'cancelled' },
                { label: 'Partially Refunded', value: 'partially_refunded' },
                { label: 'Refunded', value: 'refunded' },
              ]}
            />
            <DatePicker.RangePicker
              placeholder={['Start date', 'End date']}
              onChange={setDateRange}
              style={{ width: 250 }}
            />
          </Space>
        }
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab="Orders" key="orders">
            <Table
              columns={columns}
              dataSource={orders}
              rowKey="id"
              rowClassName={getRowClassName}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
              }}
              scroll={{ x: 1200 }}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="Billing" key="billing">
            <Table
              columns={billingColumns}
              dataSource={billingData}
              loading={billingLoading}
              rowKey="id"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} invoices`,
              }}
            />
          </Tabs.TabPane>
        </Tabs>
      </Card>

      {/* Order Details Modal */}
      <Modal
        title="Order Details"
        open={isDetailModalVisible}
        onCancel={() => {
          setIsDetailModalVisible(false);
          setViewingOrder(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsDetailModalVisible(false);
              setViewingOrder(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={800}
      >
        {viewingOrder && (
          <div style={{ padding: '20px 0' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Card size="small">
                  <h4>Order Information</h4>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <strong>Order ID:</strong> #{viewingOrder.order_number}
                    </div>
                    <div>
                      <strong>Customer:</strong>{' '}
                      {viewingOrder.customer?.name || viewingOrder.customer?.full_name}
                    </div>
                    <div>
                      <strong>Provider:</strong>{' '}
                      {viewingOrder.provider?.name || viewingOrder.provider?.full_name}
                    </div>
                    <div>
                      <strong>Order Date:</strong>{' '}
                      {new Date(viewingOrder.created_at).toLocaleString()}
                    </div>
                    <div>
                      <strong>Order Status:</strong>
                      <Tag color={viewingOrder.order_status === 'completed' ? 'green' : 'orange'}>
                        {viewingOrder.order_status?.replace('_', ' ').toUpperCase()}
                      </Tag>
                    </div>
                    <div>
                      <strong>Payment Status:</strong>
                      <Tag color={viewingOrder.payment_status === 'paid' ? 'green' : 'orange'}>
                        {viewingOrder.payment_status?.toUpperCase()}
                      </Tag>
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <h4>Financial Information</h4>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <strong>Subtotal:</strong> Rs.{' '}
                      {parseFloat(viewingOrder.subtotal || 0).toFixed(2)}
                    </div>
                    <div>
                      <strong>Delivery Charge:</strong> Rs.{' '}
                      {parseFloat(viewingOrder.delivery_charge || 0).toFixed(2)}
                    </div>
                    <div>
                      <strong>Tax:</strong> Rs. {parseFloat(viewingOrder.tax || 0).toFixed(2)}
                    </div>
                    <div>
                      <strong>Total Amount:</strong> Rs.{' '}
                      {parseFloat(viewingOrder.total_amount || 0).toFixed(2)}
                    </div>
                    <div>
                      <strong>Payment Method:</strong> {viewingOrder.payment_method || 'N/A'}
                    </div>
                    <div>
                      <strong>Refund Status:</strong>{' '}
                      <Tag color={getRefundStatusColor(viewingOrder.refund_status)}>
                        {viewingOrder.refund_status_display || 'No Refund'}
                      </Tag>
                    </div>
                    {viewingOrder.refunds && viewingOrder.refunds.length > 0 && (
                      <div>
                        <strong>Refunds:</strong>
                        <div style={{ marginTop: 4 }}>
                          {viewingOrder.refunds.map((refund, idx) => (
                            <div
                              key={refund.id}
                              style={{
                                fontSize: 13,
                                marginBottom: 4,
                                padding: '4px 8px',
                                background: '#f5f5f5',
                                borderRadius: 4,
                              }}
                            >
                              <div>
                                <strong>#{idx + 1}</strong> —{' '}
                                {refund.type_label || refund.refund_type} — Rs.{' '}
                                {parseFloat(refund.refund_amount).toFixed(2)}
                              </div>
                              <div style={{ color: '#666' }}>
                                Status: {refund.status_label || refund.status} | Reason:{' '}
                                {refund.reason}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </Space>
                </Card>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Card size="small">
                  <h4>Delivery Information</h4>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <div>
                          <strong>Delivery Address:</strong>{' '}
                          {viewingOrder.delivery_address?.address_line1
                            ? `${viewingOrder.delivery_address.address_line1}, ${viewingOrder.delivery_address.city || ''}`
                            : 'N/A'}
                        </div>
                        <div>
                          <strong>Delivery Person:</strong>{' '}
                          {viewingOrder.delivery_person?.name ||
                            viewingOrder.delivery_person?.full_name ||
                            'Not assigned'}
                          <div
                            style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}
                          >
                            <Select
                              placeholder="Assign delivery person"
                              style={{ width: 200 }}
                              value={assigningDeliveryId}
                              onChange={setAssigningDeliveryId}
                              allowClear
                              showSearch
                              filterOption={(input, option) =>
                                option.children?.toLowerCase().includes(input.toLowerCase())
                              }
                            >
                              {deliveryPersonnel.map((person) => (
                                <Option key={person.id} value={person.id}>
                                  {person.name || person.full_name}
                                </Option>
                              ))}
                            </Select>
                            <Button
                              type="primary"
                              size="small"
                              icon={<TruckOutlined />}
                              loading={assignDeliveryMutation.isLoading}
                              disabled={
                                !assigningDeliveryId ||
                                assigningDeliveryId === viewingOrder.delivery_person?.id
                              }
                              onClick={() =>
                                assignDeliveryMutation.mutate({
                                  orderId: viewingOrder.id,
                                  deliveryPersonId: assigningDeliveryId,
                                })
                              }
                            >
                              Assign
                            </Button>
                          </div>
                        </div>
                        <div>
                          <strong>Estimated Delivery:</strong>{' '}
                          {viewingOrder.estimated_delivery
                            ? new Date(viewingOrder.estimated_delivery).toLocaleString()
                            : 'N/A'}
                        </div>
                      </Space>
                    </Col>
                    <Col span={12}>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <div>
                          <strong>Delivery Status:</strong> {viewingOrder.delivery_status || 'N/A'}
                        </div>
                        <div>
                          <strong>Tracking Number:</strong> {viewingOrder.tracking_number || 'N/A'}
                        </div>
                        <div>
                          <strong>Special Instructions:</strong>{' '}
                          {viewingOrder.special_instructions || 'None'}
                        </div>
                      </Space>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Card size="small">
                  <h4>Order Items</h4>
                  <Table
                    dataSource={viewingOrder.items || []}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    columns={[
                      {
                        title: 'Item',
                        key: 'name',
                        render: (_, r) => r.product?.name || 'Unknown Item',
                      },
                      {
                        title: 'Type',
                        key: 'type',
                        render: (_, r) => {
                          const type = r.product?.type;
                          return (
                            <Tag color={type === 'service' ? 'blue' : 'green'}>
                              {type?.toUpperCase() || 'PRODUCT'}
                            </Tag>
                          );
                        },
                      },
                      { title: 'Qty', dataIndex: 'quantity', key: 'quantity' },
                      {
                        title: 'Price',
                        dataIndex: 'price_at_order',
                        key: 'price',
                        render: (p) => `Rs. ${parseFloat(p || 0).toFixed(2)}`,
                      },
                      {
                        title: 'Total',
                        key: 'total',
                        render: (_, r) =>
                          `Rs. ${(parseFloat(r.price_at_order || 0) * (r.quantity || 1)).toFixed(2)}`,
                      },
                    ]}
                  />
                </Card>
              </Col>
            </Row>

            {hasServiceItems(viewingOrder) && (
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Card size="small">
                    <h4>Service Information</h4>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <div>
                            <strong>Service Date:</strong>{' '}
                            {viewingOrder.service_date
                              ? new Date(viewingOrder.service_date).toLocaleDateString()
                              : 'N/A'}
                          </div>
                          <div>
                            <strong>Service Time:</strong>{' '}
                            {viewingOrder.service_time
                              ? new Date(viewingOrder.service_time).toLocaleTimeString()
                              : 'N/A'}
                          </div>
                          <div>
                            <strong>Contact Phone:</strong>{' '}
                            {viewingOrder.customer_contact_phone ||
                              viewingOrder.customer?.phone_number ||
                              viewingOrder.delivery_address?.phone_number ||
                              'N/A'}
                          </div>
                        </Space>
                      </Col>
                      <Col span={12}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <div>
                            <strong>Service Address:</strong>{' '}
                            {viewingOrder.service_address
                              ? viewingOrder.service_address
                              : viewingOrder.delivery_address?.address_line1
                                ? `${viewingOrder.delivery_address.address_line1}, ${viewingOrder.delivery_address.city || ''}`
                                : 'N/A'}
                          </div>
                          <div>
                            <strong>Customer Name:</strong>{' '}
                            {viewingOrder.customer?.name ||
                              viewingOrder.customer?.full_name ||
                              'N/A'}
                          </div>
                        </Space>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>
            )}

            {/* QR Payment Verification Section */}
            {viewingOrder.payment_method === 'qr' && (
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Card
                    size="small"
                    title={
                      <>
                        <QrcodeOutlined /> QR Payment Details
                      </>
                    }
                  >
                    {viewingOrder.payment_status === 'pending' &&
                      !viewingOrder.payment_proof_image && (
                        <Alert
                          message="Awaiting Customer Payment"
                          description="Customer has not yet submitted payment proof for this order."
                          type="info"
                          showIcon
                          style={{ marginBottom: 16 }}
                        />
                      )}
                    <Row gutter={16}>
                      <Col span={12}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <div>
                            <strong>Transaction ID:</strong>{' '}
                            {viewingOrder.transaction_id || 'Not provided'}
                          </div>
                          <div>
                            <strong>Payment Remarks:</strong>{' '}
                            {viewingOrder.payment_remarks || 'No remarks'}
                          </div>
                          <div>
                            <strong>Submitted At:</strong>{' '}
                            {viewingOrder.payment_submitted_at
                              ? new Date(viewingOrder.payment_submitted_at).toLocaleString()
                              : 'N/A'}
                          </div>
                          {viewingOrder.payment_verified_at && (
                            <div>
                              <strong>Verified At:</strong>{' '}
                              {new Date(viewingOrder.payment_verified_at).toLocaleString()}
                            </div>
                          )}
                        </Space>
                      </Col>
                      <Col span={12}>
                        {viewingOrder.payment_proof_image ? (
                          <div>
                            <p>
                              <strong>Payment Proof:</strong>
                            </p>
                            <Image
                              src={viewingOrder.payment_proof_image}
                              alt="Payment Proof"
                              style={{ maxWidth: '100%', maxHeight: 200 }}
                            />
                          </div>
                        ) : (
                          <Alert message="No payment proof image uploaded" type="info" showIcon />
                        )}
                      </Col>
                    </Row>

                    {viewingOrder.payment_status === 'verification_pending' && (
                      <Row gutter={16} style={{ marginTop: 16 }}>
                        <Col span={24}>
                          <Alert
                            message="Payment awaiting verification"
                            description="Customer has submitted payment proof. Please verify or reject."
                            type="warning"
                            showIcon
                            action={
                              <Space direction="vertical">
                                <Button
                                  type="primary"
                                  icon={<CheckOutlined />}
                                  onClick={() => verifyPaymentMutation.mutate(viewingOrder.id)}
                                  loading={verifyPaymentMutation.isLoading}
                                >
                                  Verify Payment
                                </Button>
                                <Button
                                  danger
                                  icon={<CloseCircleOutlined />}
                                  onClick={() => {
                                    Modal.confirm({
                                      title: 'Reject Payment',
                                      content:
                                        'Are you sure you want to reject this payment? Customer can resubmit.',
                                      onOk: () =>
                                        rejectPaymentMutation.mutate({ orderId: viewingOrder.id }),
                                    });
                                  }}
                                  loading={rejectPaymentMutation.isLoading}
                                >
                                  Reject
                                </Button>
                              </Space>
                            }
                          />
                        </Col>
                      </Row>
                    )}
                  </Card>
                </Col>
              </Row>
            )}
          </div>
        )}
      </Modal>

      {/* Update Status Modal */}
      <Modal
        title="Update Order Status"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingOrder(null);
          statusForm.resetFields();
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsModalVisible(false);
              setEditingOrder(null);
              statusForm.resetFields();
            }}
          >
            Close
          </Button>,
          <Button key="submit" type="primary" onClick={() => statusForm.submit()}>
            Update Status
          </Button>,
        ]}
      >
        {editingOrder?.payment_method === 'qr' && editingOrder?.payment_status !== 'paid' && (
          <Alert
            message="Payment Not Verified"
            description="This order has QR payment pending. Update the payment status first via the order details."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <Form form={statusForm} layout="vertical" onFinish={handleStatusUpdate}>
          <Form.Item label="Current Status" name="current_status">
            <Input disabled />
          </Form.Item>
          <Form.Item
            label="New Status"
            name="order_status"
            rules={[{ required: true, message: 'Please select new status' }]}
          >
            <Select placeholder="Select new status">
              {getValidNextStatuses(editingOrder?.order_status).map((status) => (
                <Option key={status} value={status}>
                  {status === 'out_for_delivery'
                    ? 'Out for Delivery'
                    : status === 'payment_pending'
                      ? 'Payment Pending'
                      : status.charAt(0).toUpperCase() + status.slice(1)}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Billing Modal */}
      <Modal
        title="Billing Information"
        open={isBillingModalVisible}
        onCancel={() => {
          setIsBillingModalVisible(false);
          setViewingOrder(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsBillingModalVisible(false);
              setViewingOrder(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={600}
      >
        {viewingOrder && (
          <div style={{ padding: '20px 0' }}>
            <Card size="small">
              <h4>Invoice Details</h4>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <strong>Invoice Number:</strong> INV-{viewingOrder.order_number}
                </div>
                <div>
                  <strong>Invoice Date:</strong> {new Date().toLocaleDateString()}
                </div>
                <div>
                  <strong>Due Date:</strong>{' '}
                  {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                </div>
                <Divider />
                <div>
                  <strong>Billing To:</strong>{' '}
                  {viewingOrder.customer?.name || viewingOrder.customer?.full_name}
                </div>
                <div>
                  <strong>Billing Address:</strong>{' '}
                  {viewingOrder.delivery_address?.address_line1
                    ? `${viewingOrder.delivery_address.address_line1}, ${viewingOrder.delivery_address.city || ''}`
                    : 'N/A'}
                </div>
                <Divider />
                <div>
                  <strong>Subtotal:</strong> Rs. {parseFloat(viewingOrder.subtotal || 0).toFixed(2)}
                </div>
                <div>
                  <strong>Delivery Charge:</strong> Rs.{' '}
                  {parseFloat(viewingOrder.delivery_charge || 0).toFixed(2)}
                </div>
                <div>
                  <strong>Tax:</strong> Rs. {parseFloat(viewingOrder.tax || 0).toFixed(2)}
                </div>
                <div>
                  <strong>Total Amount:</strong>{' '}
                  <strong>Rs. {parseFloat(viewingOrder.total_amount || 0).toFixed(2)}</strong>
                </div>
                <Divider />
                <div>
                  <strong>Payment Status:</strong>
                  <Tag color={viewingOrder.payment_status === 'paid' ? 'green' : 'orange'}>
                    {viewingOrder.payment_status?.toUpperCase()}
                  </Tag>
                </div>
                <div>
                  <strong>Payment Method:</strong> {viewingOrder.payment_method || 'N/A'}
                </div>
              </Space>
            </Card>
          </div>
        )}
      </Modal>

      {/* Refund Modal */}
      <RefundModal
        visible={isRefundModalVisible}
        order={refundingOrder}
        onClose={() => {
          setIsRefundModalVisible(false);
          setRefundingOrder(null);
        }}
      />
    </div>
  );
};

export default Orders;
